import { Config } from '@src/core/Config';
import { EnemyKind } from '@src/ecs/components';
import { DoorKind } from '@src/game/arena/RoomLayout';
import { FloorModifier, floorMod } from '@src/game/FloorModifiers';

export interface SmallFryWeight {
  kind: EnemyKind;
  weight: number;
}

export interface RngDraw {
  next(): number;
  int(min: number, maxInclusive: number): number;
}

/**
 * Guest kinds in debut order. Imp is always unlocked. The first three are the
 * opening-floor guests; the back four land one per stretch from tier 5 so
 * every floor after the first has something the wizard has not seen yet.
 */
export const SMALL_FRY_GUESTS = [EnemyKind.Skull, EnemyKind.Slime, EnemyKind.Bat, EnemyKind.Hexer, EnemyKind.Cinder, EnemyKind.Blight, EnemyKind.Wraith] as const;

const PACK_NAME: Record<number, string> = {
  [EnemyKind.Imp]: 'IMPS',
  [EnemyKind.Skull]: 'SKULLS',
  [EnemyKind.Slime]: 'SLIMES',
  [EnemyKind.Bat]: 'BATS',
  [EnemyKind.Gargoyle]: 'GARGOYLES',
  [EnemyKind.Hexer]: 'HEXERS',
  [EnemyKind.Cinder]: 'CINDERS',
  [EnemyKind.Blight]: 'BLIGHTS',
  [EnemyKind.Wraith]: 'WRAITHS',
  [EnemyKind.Brute]: 'BRUTES',
  [EnemyKind.Spitter]: 'SPITTERS',
  [EnemyKind.Mimic]: 'MIMICS',
};

const ANNOUNCER_GROUP: Partial<Record<EnemyKind, string>> = {
  [EnemyKind.Skull]: 'new_threat_skull',
  [EnemyKind.Slime]: 'new_threat_slime',
  [EnemyKind.Bat]: 'new_threat_bat',
  [EnemyKind.Hexer]: 'new_threat_hexer',
  [EnemyKind.Cinder]: 'new_threat_cinder',
  [EnemyKind.Blight]: 'new_threat_blight',
  [EnemyKind.Wraith]: 'new_threat_wraith',
};

export function smallFryPackName(kind: EnemyKind): string {
  return PACK_NAME[kind] ?? 'FIENDS';
}

export function newThreatAnnouncerGroup(kind: EnemyKind): string | undefined {
  return ANNOUNCER_GROUP[kind];
}

/** Debut tier for a guest. Haunted floors pull wraiths forward to the floor's opening room. */
export function guestMinTier(kind: EnemyKind, modifier: FloorModifier = FloorModifier.None): number {
  const e = Config.enemies;
  switch (kind) {
    case EnemyKind.Skull:
      return e.skull.minTier;
    case EnemyKind.Slime:
      return e.slime.minTier;
    case EnemyKind.Bat:
      return e.bat.minTier;
    case EnemyKind.Hexer:
      return e.hexer.minTier;
    case EnemyKind.Cinder:
      return e.cinder.minTier;
    case EnemyKind.Blight:
      return e.blight.minTier;
    case EnemyKind.Wraith:
      return modifier === FloorModifier.Haunted ? Math.min(e.wraith.minTier, 3) : e.wraith.minTier;
    default:
      return 1;
  }
}

export function isSmallFryUnlocked(kind: EnemyKind, tier: number, modifier: FloorModifier = FloorModifier.None): boolean {
  return kind === EnemyKind.Imp || tier >= guestMinTier(kind, modifier);
}

/** Unlocked small fry at this tier, Imp first, then guests in debut order. */
export function unlockedSmallFry(tier: number, modifier: FloorModifier = FloorModifier.None): EnemyKind[] {
  const out: EnemyKind[] = [EnemyKind.Imp];
  for (let i = 0; i < SMALL_FRY_GUESTS.length; i++) {
    const kind = SMALL_FRY_GUESTS[i];
    if (tier >= guestMinTier(kind, modifier)) out.push(kind);
  }
  return out;
}

/**
 * The new guest this stretch is selling. Imp on the opening rooms. Picks by debut
 * tier (not list order) so a Haunted floor's early wraith does not hijack every room.
 */
export function stretchFeatured(tier: number, modifier: FloorModifier = FloorModifier.None): EnemyKind {
  let newest: EnemyKind = EnemyKind.Imp;
  let newestTier = 0;
  for (let i = 0; i < SMALL_FRY_GUESTS.length; i++) {
    const kind = SMALL_FRY_GUESTS[i];
    const at = guestMinTier(kind, FloorModifier.None);
    if (tier >= at && at >= newestTier && isSmallFryUnlocked(kind, tier, modifier)) {
      newest = kind;
      newestTier = at;
    }
  }
  return newest;
}

/**
 * One featured kind per room so a portal burst reads as a pack, not a smoothie.
 * Debut tiers lock to the new guest. Hazard always spotlights the newest.
 * Treasure prefers imps. Later floors still bias newest but can star an older guest.
 * Gauntlets star whichever guest is fastest so the wall guns have company that closes.
 */
export function pickRoomFeatured(tier: number, door: DoorKind, rng: RngDraw, modifier: FloorModifier = FloorModifier.None): EnemyKind {
  const newest = stretchFeatured(tier, modifier);
  if (newest === EnemyKind.Imp) return EnemyKind.Imp;

  // How long the newest guest has been on the bill. Fresh guests headline; once the
  // roster is full the spotlight rotates so a late run is not wall-to-wall wraiths.
  const since = tier - guestMinTier(newest, modifier);
  const older: EnemyKind[] = [];
  for (let i = 0; i < SMALL_FRY_GUESTS.length; i++) {
    const kind = SMALL_FRY_GUESTS[i];
    if (kind !== newest && isSmallFryUnlocked(kind, tier, modifier)) older.push(kind);
  }
  const anyOlder = (): EnemyKind => (older.length === 0 ? newest : older[rng.int(0, older.length - 1)]);

  if (door === DoorKind.Hazard) return since <= 3 || rng.next() < 0.45 ? newest : anyOlder();
  if (door === DoorKind.Treasure) return rng.next() < 0.55 ? EnemyKind.Imp : since <= 3 || rng.next() < 0.5 ? newest : anyOlder();
  if (door === DoorKind.Gauntlet) {
    if (isSmallFryUnlocked(EnemyKind.Cinder, tier, modifier) && rng.next() < 0.5) return EnemyKind.Cinder;
    if (isSmallFryUnlocked(EnemyKind.Bat, tier, modifier) && rng.next() < 0.6) return EnemyKind.Bat;
    return EnemyKind.Imp;
  }

  if (since === 0) return newest;

  const newestBias = since <= 2 ? 0.62 : since <= 5 ? 0.45 : 0.28;
  if (rng.next() < newestBias) return newest;
  return anyOlder();
}

export function featuredBurstWeight(tier: number, door: DoorKind, featured: EnemyKind, modifier: FloorModifier = FloorModifier.None): number {
  const mix = Config.enemies.spawnMix;
  if (featured === EnemyKind.Imp) return 1;
  if (door === DoorKind.Hazard) return mix.featuredHazard;
  if (door === DoorKind.Treasure) return mix.featuredTreasure;

  const base = door === DoorKind.Exit ? mix.featuredExit : mix.featuredCombat;
  if (tier < mix.featuredLateFromTier || featured !== stretchFeatured(tier, modifier)) return base;
  return mix.featuredLate;
}

/**
 * Burst pick weights for a room. Featured kind takes the lion's share;
 * leftover stays mostly on imps, with a trickle of older guests. Haunted floors
 * then carve a fixed slice off everything for wraiths.
 */
export function smallFryWeights(tier: number, door: DoorKind, featured: EnemyKind, modifier: FloorModifier = FloorModifier.None): SmallFryWeight[] {
  const rows = baseWeights(tier, door, featured, modifier);
  const wraithW = floorMod(modifier).wraithWeight;
  if (wraithW <= 0 || featured === EnemyKind.Wraith) return rows;
  for (let i = 0; i < rows.length; i++) rows[i].weight *= 1 - wraithW;
  const existing = rows.find((r) => r.kind === EnemyKind.Wraith);
  if (existing) existing.weight += wraithW;
  else rows.push({ kind: EnemyKind.Wraith, weight: wraithW });
  return rows;
}

function baseWeights(tier: number, door: DoorKind, featured: EnemyKind, modifier: FloorModifier): SmallFryWeight[] {
  const unlocked = unlockedSmallFry(tier, modifier);
  const star = unlocked.includes(featured) ? featured : EnemyKind.Imp;
  if (star === EnemyKind.Imp || unlocked.length === 1) {
    return [{ kind: EnemyKind.Imp, weight: 1 }];
  }

  const featW = featuredBurstWeight(tier, door, star, modifier);
  const leftover = Math.max(0, 1 - featW);
  const rows: SmallFryWeight[] = [{ kind: star, weight: featW }];

  const others: EnemyKind[] = [];
  for (let i = 0; i < unlocked.length; i++) {
    if (unlocked[i] !== star) others.push(unlocked[i]);
  }

  const guests: EnemyKind[] = [];
  let hasImp = false;
  for (let i = 0; i < others.length; i++) {
    if (others[i] === EnemyKind.Imp) hasImp = true;
    else guests.push(others[i]);
  }

  if (!hasImp && guests.length === 0) return rows;

  if (hasImp && guests.length === 0) {
    rows.push({ kind: EnemyKind.Imp, weight: leftover });
    return rows;
  }

  if (hasImp) {
    const impW = leftover * Config.enemies.spawnMix.leftoverImpShare;
    rows.push({ kind: EnemyKind.Imp, weight: impW });
    const each = (leftover - impW) / guests.length;
    for (let i = 0; i < guests.length; i++) rows.push({ kind: guests[i], weight: each });
    return rows;
  }

  const each = leftover / guests.length;
  for (let i = 0; i < guests.length; i++) rows.push({ kind: guests[i], weight: each });
  return rows;
}

export function rollSmallFry(weights: readonly SmallFryWeight[], roll: number): EnemyKind {
  let acc = 0;
  for (let i = 0; i < weights.length; i++) {
    acc += weights[i].weight;
    if (roll < acc) return weights[i].kind;
  }
  return weights[weights.length - 1]?.kind ?? EnemyKind.Imp;
}

/**
 * Which heavy a portal coughs on its heavy beat. Brutes take every other heavy once
 * they debut (earlier on Elite floors); before that it is always a golem.
 */
export function heavyKind(tier: number, heavyIndex: number, modifier: FloorModifier = FloorModifier.None): EnemyKind {
  const rule = floorMod(modifier);
  const from = rule.bruteFromTier > 0 ? rule.bruteFromTier : Config.enemies.brute.minTier;
  if (tier < from) return EnemyKind.Golem;
  return heavyIndex % 2 === 1 ? EnemyKind.Brute : EnemyKind.Golem;
}

/** Format a weight table for logs / smoke tests. */
export function formatWeightTable(weights: readonly SmallFryWeight[]): string {
  return weights.map((row) => `${smallFryPackName(row.kind)} ${(row.weight * 100).toFixed(1)}%`).join('  ');
}
